/**
 * JWT Token Utilities for validation and parsing
 */

import { STORAGE_TOKEN, STORAGE_USERNAME } from '../constants/systemConstants';

/**
 * Decodes a JWT token and returns the payload
 * @param {string} token - The JWT token to decode
 * @returns {object|null} - The decoded payload or null if invalid
 */
export const decodeJWTToken = (token) => {
    if (!token || typeof token !== 'string') {
        return null;
    }

    try {
        // Split the token and get the payload part
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }

        const base64Url = parts[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            window.atob(base64).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join('')
        );

        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Error decoding JWT token:', error);
        return null;
    }
};

/**
 * Validates if the JWT token has valid authorities
 * @param {string} token - The JWT token to validate
 * @returns {boolean} - True if authorities exist and are not empty, false otherwise
 */
export const validateTokenAuthorities = (token) => {
    const payload = decodeJWTToken(token);
    
    if (!payload) {
        return false;
    }

    // Check for authorities in common JWT claim names
    const authorities = payload.authorities || payload.roles || payload.groups || payload.scope;
    
    // Authorities should exist and not be empty
    if (!authorities) {
        return false;
    }

    // Handle different authority formats
    if (Array.isArray(authorities)) {
        return authorities.length > 0;
    }

    if (typeof authorities === 'string') {
        return authorities.trim().length > 0;
    }

    // If authorities is an object, check if it has any properties
    if (typeof authorities === 'object') {
        return Object.keys(authorities).length > 0;
    }

    return false;
};

/**
 * Gets the authorities from a JWT token
 * @param {string} token - The JWT token
 * @returns {array|string|object|null} - The authorities or null if not found
 */
export const getTokenAuthorities = (token) => {
    const payload = decodeJWTToken(token);
    
    if (!payload) {
        return null;
    }

    return payload.authorities || payload.roles || payload.groups || payload.scope || null;
};

/**
 * Gets the user email from a JWT token
 * @param {string} token - The JWT token
 * @returns {string|null} - The user email or null if not found
 */
export const getTokenEmail = (token) => {
    const payload = decodeJWTToken(token);
    
    if (!payload) {
        return null;
    }

    // Check for email in common JWT claim names
    return payload.email || payload.preferred_username || payload.upn || payload.unique_name || null;
};

/**
 * Creates an unauthorized message with user email information
 * @param {string} token - The JWT token (optional)
 * @returns {string} - Formatted unauthorized message
 */
export const createUnauthorizedMessage = (token = null) => {
    let email = null;
    
    if (token) {
        email = getTokenEmail(token);
    }
    
    if (email) {
        return `Your Azure account (${email}) is not authorized to access this application.\nPlease contact your administrator to request access or try signing in with a different account.`;
    } else {
        return `Your Azure account is not authorized to access this application.\nPlease contact your administrator to request access or try signing in with a different account.`;
    }
};

/**
 * Clears MSAL cache and local storage for account switching
 * @param {object} msalInstance - The MSAL instance
 */
export const clearAuthenticationCache = (msalInstance, aggressive = false) => {
    try {
        console.log('Clearing authentication cache for account switching...');
        
        // Clear the active account to allow user to select a different one
        msalInstance.setActiveAccount(null);
        console.log('Active account cleared');
        
        if (aggressive) {
            // Only do aggressive clearing if explicitly requested
            console.log('Performing aggressive cache clearing...');
            
            // Clear all MSAL accounts
            const accounts = msalInstance.getAllAccounts();
            console.log(`Removing ${accounts.length} cached accounts`);
            accounts.forEach(account => {
                console.log(`Removing account: ${account.username}`);
                msalInstance.removeAccount(account);
            });
            
            // Clear all token cache
            const tokenCache = msalInstance.getTokenCache();
            if (tokenCache && typeof tokenCache.clear === 'function') {
                tokenCache.clear();
                console.log('MSAL token cache cleared');
            }
            
            // Clear any MSAL-specific session storage keys
            const sessionKeys = Object.keys(sessionStorage);
            sessionKeys.forEach(key => {
                if (key.includes('msal') || key.includes('login.microsoftonline.com')) {
                    console.log(`Clearing MSAL session key: ${key}`);
                    sessionStorage.removeItem(key);
                }
            });
        } else {
            // Gentle clearing - just clear tokens but preserve account info for MSAL to work
            console.log('Performing gentle cache clearing...');
            
            // Clear only access tokens, not the account cache itself
            const accounts = msalInstance.getAllAccounts();
            accounts.forEach(account => {
                // Clear tokens for each account but keep the account info
                try {
                    const silentRequest = {
                        scopes: ["user.read"],
                        account: account,
                        forceRefresh: true
                    };
                    // This clears cached tokens for the account without removing the account
                    msalInstance.acquireTokenSilent(silentRequest).catch(() => {
                        // Expected to fail, just clearing cache
                        console.log(`Token cache cleared for account: ${account.username}`);
                    });
                } catch (e) {
                    console.log(`Cleared tokens for account: ${account.username}`);
                }
            });
        }
        
        // Always clear application storage items
        localStorage.removeItem(STORAGE_TOKEN);
        localStorage.removeItem('STORAGE_IS_SSO');
        localStorage.removeItem(STORAGE_USERNAME);
        
        // Clear session storage as well
        sessionStorage.removeItem(STORAGE_TOKEN);
        sessionStorage.removeItem(STORAGE_USERNAME);
        
        console.log('Authentication cache cleared successfully');
    } catch (error) {
        console.error('Error clearing authentication cache:', error);
    }
};

/**
 * Debug function to log current authentication state
 * @param {object} msalInstance - The MSAL instance
 * @param {string} context - Context for the debug log
 */
export const debugAuthenticationState = (msalInstance, context = '') => {
    if (process.env.NODE_ENV === 'development') {
        console.log(`=== Authentication Debug ${context} ===`);
        
        const allAccounts = msalInstance.getAllAccounts();
        const activeAccount = msalInstance.getActiveAccount();
        
        console.log('All accounts:', allAccounts.map(acc => ({
            username: acc.username,
            homeAccountId: acc.homeAccountId,
            localAccountId: acc.localAccountId
        })));
        
        console.log('Active account:', activeAccount ? {
            username: activeAccount.username,
            homeAccountId: activeAccount.homeAccountId,
            localAccountId: activeAccount.localAccountId
        } : 'None');
        
        console.log('Local storage token:', localStorage.getItem(STORAGE_TOKEN) ? 'Present' : 'None');
        console.log('Session storage token:', sessionStorage.getItem(STORAGE_TOKEN) ? 'Present' : 'None');
        console.log('SSO flag:', localStorage.getItem('STORAGE_IS_SSO'));
        
        console.log('=== End Authentication Debug ===');
    }
};

/**
 * Clear all authentication data aggressively - use only when account switching fails
 * @param {object} msalInstance - The MSAL instance
 */
export const clearAllAuthenticationData = (msalInstance) => {
    return clearAuthenticationCache(msalInstance, true);
};

// Export utilities to window object for development debugging
if (process.env.NODE_ENV === 'development') {
    window.decodeJWTToken = decodeJWTToken;
    window.validateTokenAuthorities = validateTokenAuthorities;
    window.getTokenAuthorities = getTokenAuthorities;
    window.getTokenEmail = getTokenEmail;
    window.createUnauthorizedMessage = createUnauthorizedMessage;
    window.clearAuthenticationCache = clearAuthenticationCache;
    window.clearAllAuthenticationData = clearAllAuthenticationData;
    window.debugAuthenticationState = debugAuthenticationState;
}
